import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'

import { useShowMessage } from '@pepper/ui'
import { fetchProjectTemplateList } from '@pepper/utils/src/api/projects/projects'

type ReturnType = {
  records: ProjectTemplateList[]
  totalRecords: number
  currentPage: number
  isLoading: boolean
  isFetching: boolean
  refetch: () => void
  error: Error | null
}

type DataModal = {
  records: ProjectTemplateList[]
  total_records: number
}

export const usePaginatedDraftsList = (
  queryParams: Partial<ProjectQueryParams>,
  pageSize: number,
  currentPage = 0,
  enabled = true
): ReturnType => {
  const showMessage = useShowMessage()

  const handleApiRequest = async () => {
    const {
      status,
      data,
      error: draftError,
    } = await fetchProjectTemplateList({
      query_params: queryParams,
      page: currentPage,
      page_size: pageSize,
      type: 'draft',
    })

    if (status === 'error') {
      throw new Error(draftError)
    }

    return data
  }

  const { isLoading, isFetching, isError, error, refetch, data } = useQuery<
    DataModal,
    Error
  >({
    queryKey: ['project-drafts', currentPage, queryParams],
    queryFn: handleApiRequest,
    enabled,
    retry: true,
    cacheTime: 5000,
    keepPreviousData: true,
  })

  useEffect(() => {
    if (isError) {
      showMessage({ type: 'error', message: error?.message })
    }
  }, [isError, error, showMessage])

  return {
    records: data?.records ?? [],
    totalRecords: data?.total_records ?? 0,
    currentPage: queryParams.current_page || 0,
    isLoading,
    isFetching,
    refetch,
    error,
  }
}
